/**
 * IngestionControl — Management Portal page
 * Stream C1 / Story #3503
 *
 * Operator control plane for patent and journal ingestion pipelines.
 * Supports all environments (Dev/Test/UAT/Prod) via tab selector.
 * Polls status every 30 s; allows Start, Stop, and View Logs actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Play, Square, RefreshCw, ExternalLink, Clock,
  CheckCircle2, AlertTriangle, Loader2, FileText,
  Activity, BookOpen, ChevronRight,
} from 'lucide-react';
import { ingestionApi, ENV_COLORS } from '../services/api';

// ── Types ────────────────────────────────────────────────────────────────────

export type PipelineId = 'patent' | 'journal';

export type IngestionStatus =
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'ABORTED'
  | 'TIMED_OUT'
  | 'IDLE'
  | 'UNKNOWN';

export interface PipelineState {
  pipelineId: PipelineId;
  status: IngestionStatus;
  executionArn: string | null;
  lastRun: string | null;
  durationSeconds: number | null;
  papersProcessed: number | null;
  errorMessage: string | null;
}

export interface PipelineAction {
  pipelineId: PipelineId;
  action: 'start' | 'stop' | 'status' | 'get_logs' | 'list_executions';
  environment: string;
  sinceDate?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ENVIRONMENTS = ['dev', 'test', 'uat', 'prod'] as const;
type Env = typeof ENVIRONMENTS[number];

const PIPELINES: { id: PipelineId; label: string; icon: React.FC<{ className?: string; style?: React.CSSProperties }> }[] = [
  { id: 'patent', label: 'Patent Ingestion', icon: FileText },
  { id: 'journal', label: 'Journal Ingestion', icon: BookOpen },
];

const POLL_INTERVAL_MS = 30_000;

function emptyPipeline(id: PipelineId): PipelineState {
  return {
    pipelineId: id,
    status: 'UNKNOWN',
    executionArn: null,
    lastRun: null,
    durationSeconds: null,
    papersProcessed: null,
    errorMessage: null,
  };
}

// ── StatusBadge ──────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<IngestionStatus, string> = {
  RUNNING:    'bg-blue-100 text-blue-800',
  SUCCEEDED:  'bg-green-100 text-green-800',
  FAILED:     'bg-red-100 text-red-800',
  ABORTED:    'bg-yellow-100 text-yellow-800',
  TIMED_OUT:  'bg-orange-100 text-orange-800',
  IDLE:       'bg-gray-100 text-gray-600',
  UNKNOWN:    'bg-gray-100 text-gray-400',
};

const StatusBadge: React.FC<{ status: IngestionStatus }> = ({ status }) => {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.UNKNOWN;
  const icons: Partial<Record<IngestionStatus, React.ReactNode>> = {
    RUNNING:   <Loader2 className="w-3 h-3 animate-spin mr-1" />,
    SUCCEEDED: <CheckCircle2 className="w-3 h-3 mr-1" />,
    FAILED:    <AlertTriangle className="w-3 h-3 mr-1" />,
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style}`}>
      {icons[status]}
      {status}
    </span>
  );
};

// ── PipelineCard ──────────────────────────────────────────────────────────────

interface PipelineCardProps {
  pipeline: { id: PipelineId; label: string; icon: React.FC<{ className?: string; style?: React.CSSProperties }> };
  state: PipelineState;
  environment: string;
  envColors: { primary: string; bg: string; text: string };
  actionLoading: PipelineId | null;
  onStart: (id: PipelineId) => void;
  onStop: (id: PipelineId) => void;
  onViewLogs: (id: PipelineId) => void;
}

const PipelineCard: React.FC<PipelineCardProps> = ({
  pipeline, state, environment, envColors, actionLoading,
  onStart, onStop, onViewLogs,
}) => {
  const Icon = pipeline.icon;
  const isLoading = actionLoading === pipeline.id;
  const isRunning = state.status === 'RUNNING';

  const formatDuration = (secs: number | null) => {
    if (secs === null) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-AU', {
        dateStyle: 'short', timeStyle: 'short', timeZone: 'Australia/Sydney',
      });
    } catch {
      return iso;
    }
  };

  const cwLogsUrl = state.executionArn
    ? `https://ap-southeast-2.console.aws.amazon.com/cloudwatch/home?region=ap-southeast-2#logsV2:log-groups/log-group/%252Faws%252Fstates%252Fsecureembed-${environment}-${pipeline.id}-ingestion`
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: envColors.bg }}>
            <Icon className="w-5 h-5" style={{ color: envColors.primary }} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{pipeline.label}</h3>
            <p className="text-xs text-gray-500 capitalize">{environment} environment</p>
          </div>
        </div>
        <StatusBadge status={state.status} />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-50 rounded p-3">
          <p className="text-xs text-gray-500 mb-1">Last Run</p>
          <p className="text-sm font-medium text-gray-800">{formatDate(state.lastRun)}</p>
        </div>
        <div className="bg-gray-50 rounded p-3">
          <p className="text-xs text-gray-500 mb-1">Duration</p>
          <p className="text-sm font-medium text-gray-800">{formatDuration(state.durationSeconds)}</p>
        </div>
        <div className="bg-gray-50 rounded p-3">
          <p className="text-xs text-gray-500 mb-1">Papers</p>
          <p className="text-sm font-medium text-gray-800">
            {state.papersProcessed !== null ? state.papersProcessed.toLocaleString() : '—'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {state.errorMessage && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 break-all">
          {state.errorMessage}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {!isRunning ? (
          <button
            onClick={() => onStart(pipeline.id)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50"
            style={{ backgroundColor: envColors.primary }}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Start Run
          </button>
        ) : (
          <button
            onClick={() => onStop(pipeline.id)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
            Stop Run
          </button>
        )}
        <button
          onClick={() => onViewLogs(pipeline.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          <Activity className="w-4 h-4" />
          View Logs
        </button>
        {cwLogsUrl && (
          <a
            href={cwLogsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors ml-auto"
          >
            CloudWatch
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};

// ── LogsPanel ─────────────────────────────────────────────────────────────────

interface LogsPanelProps {
  pipelineId: PipelineId;
  environment: string;
  onClose: () => void;
}

const LogsPanel: React.FC<LogsPanelProps> = ({ pipelineId, environment, onClose }) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const res = await ingestionApi.getLogs({ env: environment });
        const lines: string[] = Array.isArray(res?.logs)
          ? res.logs.filter((l: any) => typeof l === 'string')
          : [JSON.stringify(res, null, 2)];
        setLogs(lines.slice(-200));
      } catch (err: any) {
        setError(err?.message || 'Failed to load logs');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [pipelineId, environment]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-800 capitalize">
            {pipelineId} Ingestion Logs — {environment}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-900 rounded-b-lg font-mono text-xs text-green-400">
          {loading && <div className="text-gray-400">Loading logs…</div>}
          {error && <div className="text-red-400">{error}</div>}
          {!loading && !error && logs.map((line, i) => (
            <div key={i} className="whitespace-pre-wrap">{line}</div>
          ))}
          {!loading && !error && logs.length === 0 && (
            <div className="text-gray-500">No log entries found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── IngestionControl (main page) ──────────────────────────────────────────────

const IngestionControl: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [environment, setEnvironment] = useState<Env>('dev');
  const [pipelines, setPipelines] = useState<Record<PipelineId, PipelineState>>({
    patent: emptyPipeline('patent'),
    journal: emptyPipeline('journal'),
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<PipelineId | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logsPanel, setLogsPanel] = useState<PipelineId | null>(null);

  const envColors = ENV_COLORS[environment];

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ingestionApi.getStatus({ env: environment });
      const update: Record<PipelineId, PipelineState> = {
        patent: emptyPipeline('patent'),
        journal: emptyPipeline('journal'),
      };
      // Map API response fields onto our PipelineState shape
      for (const id of ['patent', 'journal'] as PipelineId[]) {
        const raw = res?.[id] ?? res?.pipelines?.[id] ?? {};
        update[id] = {
          pipelineId: id,
          status: raw.status ?? raw.executionStatus ?? 'UNKNOWN',
          executionArn: raw.executionArn ?? raw.execution_arn ?? null,
          lastRun: raw.lastRun ?? raw.last_run ?? raw.startDate ?? null,
          durationSeconds: raw.durationSeconds ?? raw.duration_seconds ?? null,
          papersProcessed: raw.papersProcessed ?? raw.papers_processed ?? raw.recordCount ?? null,
          errorMessage: raw.errorMessage ?? raw.error ?? null,
        };
      }
      setPipelines(update);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch ingestion status');
    } finally {
      setLoading(false);
    }
  }, [environment]);

  // Initial load + polling
  useEffect(() => {
    fetchStatus();
    const timer = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleStart = async (pipelineId: PipelineId) => {
    setActionLoading(pipelineId);
    try {
      await ingestionApi.run({ env: environment, datastore: pipelineId });
      await fetchStatus();
    } catch (err: any) {
      setError(`Failed to start ${pipelineId}: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStop = async (pipelineId: PipelineId) => {
    setActionLoading(pipelineId);
    try {
      await ingestionApi.stop({ env: environment });
      await fetchStatus();
    } catch (err: any) {
      setError(`Failed to stop ${pipelineId}: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Page header */}
      {!embedded && (
        <div className="flex items-center gap-2 mb-6">
          <Activity className="w-6 h-6 text-gray-600" />
          <div>
            <div className="flex items-center gap-1 text-xs text-gray-400 mb-0.5">
              <span>Platform</span>
              <ChevronRight className="w-3 h-3" />
              <span>Ingestion Control</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Ingestion Control</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Trigger, monitor, and stop patent and journal ingestion pipelines.
            </p>
          </div>
        </div>
      )}

      {/* Environment tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {ENVIRONMENTS.map(env => {
          const active = env === environment;
          const colors = ENV_COLORS[env];
          return (
            <button
              key={env}
              onClick={() => setEnvironment(env)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${
                active
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={active ? { color: colors.primary } : {}}
            >
              {env.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {lastRefreshed && (
            <>
              <Clock className="w-4 h-4" />
              <span>Last refreshed: {lastRefreshed.toLocaleTimeString('en-AU')}</span>
              <span className="text-gray-300">·</span>
              <span>Auto-refreshes every 30s</span>
            </>
          )}
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Pipeline cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PIPELINES.map(pipeline => (
          <PipelineCard
            key={pipeline.id}
            pipeline={pipeline}
            state={pipelines[pipeline.id]}
            environment={environment}
            envColors={envColors}
            actionLoading={actionLoading}
            onStart={handleStart}
            onStop={handleStop}
            onViewLogs={(id) => setLogsPanel(id)}
          />
        ))}
      </div>

      {/* Logs modal */}
      {logsPanel && (
        <LogsPanel
          pipelineId={logsPanel}
          environment={environment}
          onClose={() => setLogsPanel(null)}
        />
      )}
    </div>
  );
};

export default IngestionControl;
